import { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Collapse,
  TextField,
  Button,
  Avatar,
  Chip,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RequestChangesIcon,
  Comment as CommentIcon,
} from '@material-ui/icons';
import { PullRequestReviewItem } from '../../types';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.background.default
      : theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    maxHeight: 400,
    overflow: 'auto',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.background.default
      : theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  closeButton: {
    padding: theme.spacing(0.5),
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  error: {
    padding: theme.spacing(2),
    color: theme.palette.error.main,
  },
  noReviews: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  review: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  avatar: {
    width: 32,
    height: 32,
  },
  reviewContent: {
    flex: 1,
    minWidth: 0,
  },
  reviewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  authorName: {
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  stateChip: {
    height: 20,
    fontSize: '0.7rem',
  },
  approvedChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  changesRequestedChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  commentedChip: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.getContrastText(theme.palette.grey[400]),
  },
  dismissedChip: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.secondary,
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  reviewBody: {
    fontSize: '0.875rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    '& code': {
      backgroundColor: theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
      padding: '2px 4px',
      borderRadius: 4,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      fontSize: '0.8rem',
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
    },
  },
  approveButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[300],
    },
  },
  requestChangesButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[300],
    },
  },
}));

interface ReviewsViewerProps {
  reviews: PullRequestReviewItem[] | null;
  loading: boolean;
  error: string | null;
  open: boolean;
  onClose: () => void;
  onAddReview: (body: string, event: 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES') => Promise<void>;
  submitting: boolean;
  canApprove: boolean; // False if user is the PR author (can't self-approve)
  canRequestChanges: boolean;
}

export const ReviewsViewer = ({
  reviews,
  loading,
  error,
  open,
  onClose,
  onAddReview,
  submitting,
  canApprove,
  canRequestChanges,
}: ReviewsViewerProps) => {
  const classes = useStyles();
  const [newReview, setNewReview] = useState('');
  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && textFieldRef.current) {
      // Small delay to allow Collapse animation to start
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSubmit = async (event: 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES') => {
    await onAddReview(newReview, event);
    setNewReview('');
  };

  const handleCancel = () => {
    setNewReview('');
    onClose();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStateChipClass = (state: string) => {
    switch (state) {
      case 'APPROVED':
        return classes.approvedChip;
      case 'CHANGES_REQUESTED':
        return classes.changesRequestedChip;
      case 'COMMENTED':
        return classes.commentedChip;
      case 'DISMISSED':
        return classes.dismissedChip;
      default:
        return classes.commentedChip;
    }
  };

  const getStateLabel = (state: string) => {
    switch (state) {
      case 'APPROVED':
        return 'Approved';
      case 'CHANGES_REQUESTED':
        return 'Changes requested';
      case 'COMMENTED':
        return 'Commented';
      case 'DISMISSED':
        return 'Dismissed';
      case 'PENDING':
        return 'Pending';
      default:
        return state;
    }
  };

  const canComment = newReview.length >= 2 && !submitting;

  return (
    <Collapse in={open}>
      <Paper className={classes.container} elevation={0}>
        <Box className={classes.header}>
          <Typography variant="subtitle2">
            Reviews ({reviews?.length || 0})
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            className={classes.closeButton}
            aria-label="Close reviews"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.content}>
          {loading && (
            <Box className={classes.loading}>
              <CircularProgress size={24} />
              <Typography variant="body2" style={{ marginLeft: 8 }}>
                Loading reviews...
              </Typography>
            </Box>
          )}

          {error && (
            <Box className={classes.error}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          {/* Empty state removed - count already shown in header */}

          {!loading && !error && reviews && reviews.map(review => (
            <Box key={review.id} className={classes.review}>
              <Avatar
                src={review.author.avatarUrl}
                alt={review.author.login}
                className={classes.avatar}
              />
              <Box className={classes.reviewContent}>
                <Box className={classes.reviewHeader}>
                  <Typography className={classes.authorName}>
                    {review.author.login}
                  </Typography>
                  <Chip
                    label={getStateLabel(review.state)}
                    size="small"
                    className={`${classes.stateChip} ${getStateChipClass(review.state)}`}
                  />
                  <Typography className={classes.timestamp}>
                    {formatDate(review.submittedAt || review.createdAt)}
                  </Typography>
                </Box>
                {review.body && (
                  <Typography className={classes.reviewBody}>
                    {review.body}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <Box className={classes.footer}>
          <TextField
            inputRef={textFieldRef}
            placeholder="Leave a review..."
            multiline
            rows={2}
            variant="outlined"
            size="small"
            fullWidth
            value={newReview}
            onChange={e => setNewReview(e.target.value)}
            className={classes.textField}
            disabled={submitting}
          />
          <Box className={classes.footerActions}>
            <Button
              size="small"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSubmit('COMMENT')}
              disabled={!canComment}
              startIcon={submitting ? <CircularProgress size={14} /> : <CommentIcon />}
            >
              Comment
            </Button>
            <Button
              variant="contained"
              size="small"
              className={classes.approveButton}
              onClick={() => handleSubmit('APPROVE')}
              disabled={!canApprove || submitting}
              startIcon={submitting ? <CircularProgress size={14} /> : <ApproveIcon />}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              size="small"
              className={classes.requestChangesButton}
              onClick={() => handleSubmit('REQUEST_CHANGES')}
              disabled={!canRequestChanges || submitting}
              startIcon={submitting ? <CircularProgress size={14} /> : <RequestChangesIcon />}
            >
              Request Changes
            </Button>
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
};
